import React, { useState, useRef } from 'react';
import { Header, Modal } from '../../../../components';
import { Button, message, Input } from 'antd';
import { SearchOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { useModal } from 'react-modal-hook';
import Table from '../components/Table';
import Form from '../components/Form';
import SyncModal from '../components/SyncModal';
import { JOB_TYPE } from '../../../../data/constant';
import {
  useFetchUserBankList,
  useFetchCreateUserBank,
  useFetchUpdateUserBank,
  useFetchDeleteUserBank,
} from '../hooks/useFetch';

const titlePage = 'ข้อมูลธนาคารลูกค้า';

// สิทธิ์หน้านี้ — ต้องตรงกับ userBankModel.canManage / canSync ฝั่ง backend
//
// เพิ่ม/แก้ไข/ลบ : Admin ขึ้นไป (role 1,2) + Panel(Output) คนที่ใช้ข้อมูลนี้ทำงานจริง
// ดึงจากเว็บ MGB : เฉพาะ Admin ขึ้นไป — กดทีเดียวเขียนหลายพันแถวและไปกวนเว็บปลายทาง
const isAdminOrAbove = () => {
  const role = String(sessionStorage.getItem('role'));
  return role === '1' || role === '2';
};

const canManageUserBank = () => {
  if (isAdminOrAbove()) return true;
  return Number(sessionStorage.getItem('jobType')) === JOB_TYPE.panel_output;
};

// ข้อความ error ที่ backend ส่งกลับมา (เช่น เบอร์ซ้ำ) อยู่ใน response.data.message
const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function UserBank() {
  const { currentColor } = useStateContext();
  const [dataEdit, setDataEdit] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const canManage = canManageUserBank();
  const canSync = isAdminOrAbove();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data: userBankData, isLoading, isFetching, refetch } = useFetchUserBankList({
    page,
    pageSize,
    search,
  });
  const { mutateAsync: createUserBank, isLoading: isCreating } = useFetchCreateUserBank();
  const { mutateAsync: updateUserBank, isLoading: isUpdating } = useFetchUpdateUserBank();
  const { mutateAsync: deleteUserBank } = useFetchDeleteUserBank();

  const formAddRef = useRef(null);
  const formEditRef = useRef(null);

  const handlePaginationChange = (newPage, newPageSize) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  //#region Modal hooks
  const [showAddModal, hideAddModal] = useModal(() => {
    const handleOnOk = async (event) => {
      event.preventDefault();
      try {
        const value = await formAddRef.current.validateFields();
        message.loading('กรุณารอซักครู่...', 0);
        await createUserBank(value);
        await message.destroy();
        message.success('เพิ่มข้อมูลสำเร็จ');
        formAddRef.current?.resetFields();
        hideAddModal();
        refetch();
      } catch (error) {
        await message.destroy();
        if (error?.errorFields) return; // ฟอร์มไม่ผ่าน validate — antd แสดงใต้ช่องแล้ว
        message.error(errorMessage(error, 'ไม่สามารถเพิ่มข้อมูลได้'));
      }
    };

    return (
      <Modal
        title={`เพิ่ม ${titlePage}`}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        width={700}
        loading={isCreating}
        onOk={handleOnOk}
        onCancel={() => {
          formAddRef.current?.resetFields();
          hideAddModal();
        }}
      >
        <Form formRef={formAddRef} />
      </Modal>
    );
  }, [isCreating]);

  const [showEditModal, hideEditModal] = useModal(() => {
    const handleOnOk = async (event) => {
      event.preventDefault();
      try {
        const value = await formEditRef.current.validateFields();
        message.loading('กรุณารอซักครู่...', 0);
        await updateUserBank({ ...value, id: dataEdit.id });
        await message.destroy();
        message.success('แก้ไขข้อมูลสำเร็จ');
        hideEditModal();
        refetch();
      } catch (error) {
        await message.destroy();
        if (error?.errorFields) return;
        message.error(errorMessage(error, 'ไม่สามารถแก้ไขข้อมูลได้'));
      }
    };

    return (
      <Modal
        title={`แก้ไข ${titlePage}`}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        width={700}
        loading={isUpdating}
        onOk={handleOnOk}
        onCancel={() => hideEditModal()}
      >
        <Form formRef={formEditRef} initialValues={dataEdit || {}} />
      </Modal>
    );
  }, [dataEdit, isUpdating]);
  //#endregion

  const handleEdit = (record) => {
    setDataEdit(record);
    showEditModal();
  };

  const handleDelete = async (record) => {
    try {
      message.loading('กำลังลบข้อมูล...', 0);
      await deleteUserBank(record.id);
      await message.destroy();
      message.success('ลบสำเร็จ', 2);
      refetch();
    } catch (error) {
      await message.destroy();
      message.error(errorMessage(error, 'ไม่สามารถลบได้'));
    }
  };

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-4">
        <Header category="App" title={titlePage} />
        <div className="flex gap-2">
          {canSync && (
            <Button
              onClick={() => setSyncOpen(true)}
              icon={<CloudDownloadOutlined />}
              style={{ backgroundColor: currentColor, borderColor: currentColor, color: '#fff' }}
            >
              ดึงข้อมูลจากเว็บ MGB
            </Button>
          )}
          {canManage && (
            <Button onClick={showAddModal} className="bg-blue-500 text-white">
              เพิ่มข้อมูล
            </Button>
          )}
        </div>
      </div>

      {/* ค้นหา: ตัวเลข = ค้นเบอร์/เลขบัญชีแบบขึ้นต้น (เร็ว) · ตัวอักษร = ค้นชื่อ/ธนาคาร */}
      <div className="flex gap-2 mb-1">
        <Input
          placeholder="ค้นหาเบอร์โทร เลขที่บัญชี หรือชื่อ..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          allowClear
          onClear={handleSearchClear}
        />
        <Button onClick={handleSearch} type="primary" style={{ backgroundColor: currentColor }}>
          ค้นหา
        </Button>
      </div>
      <div className="mb-4 text-xs text-gray-400">
        ใส่ตัวเลข = ค้นหาเบอร์โทร/เลขที่บัญชีจากตัวขึ้นต้น · ใส่ตัวอักษร = ค้นหาชื่อหรือธนาคาร
      </div>

      <Table
        data={userBankData?.data || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canManage={canManage}
        loading={isLoading || isFetching}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: userBankData?.total || 0,
        }}
        onPaginationChange={handlePaginationChange}
      />

      {canSync && (
      <SyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onFinished={refetch}
      />
      )}
    </div>
  );
}

export default UserBank;
